import { ContentItemViewNodesType } from '../../../types'

/**
 * Finds a node by its element ID
 * @param nodes - Array of nodes to search through
 * @param elementId - The element ID to search for
 * @returns The found node or undefined
 */
export const findNodeById = (
    nodes: Partial<ContentItemViewNodesType>[],
    elementId: string
): Partial<ContentItemViewNodesType> | undefined => {
    return nodes.find((node) =>
        node.elements?.some((element) => element.id === elementId)
    )
}

/**
 * Finds the parent node of a given child node
 * @param nodes - Array of all nodes
 * @param childNode - The child node to find parent for
 * @returns The parent node or undefined
 */
export const findParentNode = (
    nodes: Partial<ContentItemViewNodesType>[],
    childNode: Partial<ContentItemViewNodesType>
): Partial<ContentItemViewNodesType> | undefined => {
    return nodes.find((node) => {
        if (childNode.dtvContentItemParentId) {
            return node.id === childNode.dtvContentItemParentId
        }
        return node.dtvItemId === childNode.dtvParentId
    })
}

/**
 * Finds all siblings of a node (nodes with the same parent)
 * @param nodes - Array of all nodes
 * @param parentNode - The parent node (undefined for top-level items)
 * @param excludeId - Optional ID to exclude from siblings
 * @returns Array of sibling nodes
 */
export const findSiblings = (
    nodes: Partial<ContentItemViewNodesType>[],
    parentNode: Partial<ContentItemViewNodesType> | undefined,
    excludeId?: string
): Partial<ContentItemViewNodesType>[] => {
    return nodes.filter((node) => {
        if (excludeId && node.id === excludeId) return false

        if (parentNode) {
            return (
                node.dtvParentId === parentNode.dtvItemId ||
                node.dtvContentItemParentId === parentNode.id
            )
        }
        // top-level items
        return !node.dtvParentId && !node.dtvContentItemParentId
    })
}

/**
 * Finds a valid parent for demotion from the siblings
 * @param siblings - Array of sibling nodes
 * @param currentNode - The node being demoted
 * @returns A valid parent node or undefined
 */
export const findValidDemoteParent = (
    siblings: Partial<ContentItemViewNodesType>[],
    currentNode: Partial<ContentItemViewNodesType>
): Partial<ContentItemViewNodesType> | undefined => {
    // find previous siblings that can be parents (not BOD, not Manufacturers)
    const previousSiblings = siblings
        .filter(
            (s) =>
                s.order !== undefined &&
                currentNode.order !== undefined &&
                s.order < currentNode.order
        )
        .sort((a, b) => (b.order || 0) - (a.order || 0))

    return previousSiblings.find((s) => !s.isBOD && !s.isManufacturers)
}
